// Single source of truth for every scalar/string constant used inside the
// ThreadContainer feature folder. Per-component magic numbers used to live
// alongside their components — they were centralised here so layout +
// timing tweaks land in one file.

import { keyframes } from '@emotion/react';

import type { TranscriptMessage } from '../../types';

// ─── Window layout (overlay mode) ────────────────────────────────────────
export const EXPANDED_WIDTH_PX = 850;
export const COLLAPSED_WIDTH_PX = 430;
export const HEIGHT_PX = 640;
export const BOTTOM_OFFSET_PX = 56;
export const RIGHT_OFFSET_PX = 24;

// ─── Chrome / icon sizing ────────────────────────────────────────────────
/** Logo tile edge length in the Header. Icon inside renders at half this. */
export const LOGO_SIZE_PX = 24;
/** Edge length for icons inside WindowControls buttons. */
export const ICON_SIZE = 14;

// ─── Scroll behaviour ───────────────────────────────────────────────────
/** Top gap kept between the pinned user message and the header border. */
export const SCROLL_TOP_OFFSET_PX = 10;

// ─── Timers ──────────────────────────────────────────────────────────────

// ─── Animations ──────────────────────────────────────────────────────────
/** Slide + fade applied to each new message bubble on its first paint. */
export const messageEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/** Duration of `messageEnter`, kept in sync with the keyframes above. */
export const MESSAGE_ENTER_DURATION_MS = 240;

/** Width transition for the expand <-> collapse toggle on the root window. */
export const WIDTH_TRANSITION = 'width 0.3s ease';

// ─── Session adapter ─────────────────────────────────────────────────────
/** Placeholder session id used while the user is composing a draft chat. */
export const DRAFT_SESSION_ID = '__draft__';
/** Message id of the temporary bubble holding the still-streaming reply. */
export const STREAMING_DRAFT_MESSAGE_ID = '__streaming-draft__';
/** Header title rendered for an unsaved draft. */
export const DRAFT_TITLE = 'New chat';
/**
 * Stable empty array used as the fallback for missing message buckets so
 * unrelated store updates don't churn the messages-derived memo.
 */
export const EMPTY_MESSAGES: TranscriptMessage[] = [];

// Claude-Code-style idle gerunds. Used as the pre-tool-call placeholder
// label in SpinnerMessage — once the agent emits its first tool call, the
// spinner switches to a context-derived verb ("Loading candidate record…",
// "Searching Kula…") via contextualThinkingLabel(). Kept apart from the
// recruiting-themed `thinkingStates` below so we can pick voices per state.
export const claudeThinkingStates = [
  'Thinking',
  'Pondering',
  'Cogitating',
  'Deliberating',
  'Ruminating',
  'Musing',
  'Reasoning',
  'Reflecting',
  'Working',
  'Crunching',
  'Mulling',
  'Considering',
  'Brewing',
  'Simmering',
  'Marinating',
  'Percolating',
  'Noodling',
  'Pondering',
  'Synthesizing',
  'Reticulating',
  'Inferring',
  'Conjuring',
  'Forging',
  'Hatching',
  'Cooking',
  'Vibing',
  'Wrangling',
  'Tinkering',
];
